import { useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import useMediaQuery from "../../hooks/useMediaQuery";
import { MUSCLES, muscleLabel } from "./lib/format";
import { missingEquipment, primaryMuscles } from "./lib/planEdit";

const PHONE = "(max-width: 767px)";
// How many of the left-out exercises to name before "and N more".
const NAMED_UNAVAILABLE = 3;

const EQUIPMENT_WORDS = {
  barbell: "a barbell",
  rack: "a rack",
  bench: "a bench",
  adjustable_bench: "an adjustable bench",
  dumbbells: "dumbbells",
  cable: "a cable",
  pullup_bar: "a pull-up bar",
  bands: "bands",
};
const needs = (items) => items.map((item) => EQUIPMENT_WORDS[item] ?? item.replace(/_/g, " ")).join(", ");

const matches = (exercise, query, muscle) => {
  if (muscle && !(Number(exercise.muscles?.[muscle]) > 0)) return false;
  if (!query) return true;
  return exercise.name.toLowerCase().includes(query) || exercise.key.includes(query);
};

/**
 * Pick an exercise from a list — the swap and add sheets of the plan editor
 * (walkthrough 4), from the bottom on a phone and the side on a desktop.
 * Search by name and, optionally, filter by muscle; each option says what it
 * trains and what it needs. Exercises left out for want of equipment are
 * named underneath, with the way to change that (Settings), so nothing
 * silently disappears.
 *
 * The caller decides the list (best first) and what a pick does;
 * `onCloseAutoFocus` lets it send focus somewhere other than the opener
 * when a pick replaced the opener's row.
 */
function ExercisePicker({
  open,
  onOpenChange,
  title,
  description,
  options,
  unavailable = [],
  settings,
  onPick,
  muscleFilter = false,
  emptyText,
  onCloseAutoFocus,
}) {
  const isPhone = useMediaQuery(PHONE);
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const [muscle, setMuscle] = useState("");
  const id = useId();
  const needle = query.trim().toLowerCase();

  const shown = useMemo(
    () => options.filter((exercise) => matches(exercise, needle, muscle)),
    [options, needle, muscle],
  );
  const hidden = useMemo(
    () => unavailable.filter((exercise) => matches(exercise, needle, muscle)),
    [unavailable, needle, muscle],
  );

  const reset = () => {
    setQuery("");
    setMuscle("");
  };
  const change = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };
  const pick = (exercise) => {
    reset(); // a pick closes it through `open`, which onOpenChange never hears
    onPick(exercise);
  };

  return (
    <Sheet open={open} onOpenChange={change}>
      {/* data-training: portalled out of the layout's wrapper, so it
          carries the scope that keeps its muted text AA (tailwind.css). */}
      <SheetContent
        data-training=""
        side={isPhone ? "bottom" : "right"}
        showCloseButton={false}
        className={
          isPhone
            ? "max-h-[85dvh] gap-0 rounded-t-lg pb-[env(safe-area-inset-bottom)]"
            : "w-full gap-0 sm:max-w-md"
        }
        // Static backdrop (the house Dialogs contract): a stray tap on the
        // strip above the sheet mustn't close it and lose the search.
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <SheetHeader className="flex-row items-start gap-3 border-b">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SheetTitle className="font-display text-xl">{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </div>
          {/* The sheet's own X is 16px; this is the 44px way out. */}
          <Button
            type="button"
            variant="ghost"
            className="h-11 shrink-0 md:h-9"
            onClick={() => change(false)}
          >
            Cancel
          </Button>
        </SheetHeader>
        <div className="flex flex-col gap-3 border-b p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-search`}>Search exercises</Label>
            <Input
              ref={searchRef}
              id={`${id}-search`}
              type="search"
              value={query}
              placeholder="A name, e.g. curl or row"
              autoComplete="off"
              enterKeyHint="search"
              className="h-11 md:h-9"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {muscleFilter && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-muscle`}>Muscle</Label>
              <NativeSelect
                id={`${id}-muscle`}
                value={muscle}
                className="h-11 md:h-9"
                onChange={(event) => setMuscle(event.target.value)}
              >
                <option value="">Any muscle</option>
                {MUSCLES.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}
        </div>
        <div data-slot="dialog-body" className="min-h-0 flex-1 overflow-y-auto p-4">
          {shown.length === 0 ? (
            <p className="m-0 text-sm text-muted-foreground" role="status">
              {options.length === 0
                ? (emptyText ?? "Nothing here fits your equipment.")
                : "Nothing matches — try another name or muscle."}
            </p>
          ) : (
            <ul aria-label="Exercises you can pick" className="m-0 flex list-none flex-col gap-1.5 p-0">
              {shown.map((exercise) => {
                const detailId = `${id}-${exercise.key}`;
                const trains = primaryMuscles(exercise).map(muscleLabel).join(", ");
                const gear = exercise.requires?.length ? needs(exercise.requires) : "no equipment";
                return (
                  <li key={exercise.key}>
                    <button
                      type="button"
                      aria-label={exercise.name}
                      aria-describedby={detailId}
                      className="flex min-h-11 w-full flex-col items-start gap-0.5 rounded-md border border-input px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:outline-none md:min-h-9"
                      onClick={() => pick(exercise)}
                    >
                      <span className="text-sm font-medium">{exercise.name}</span>
                      <span id={detailId} className="text-xs text-muted-foreground">
                        {trains || "—"} · {gear}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {hidden.length > 0 && (
            <p className="mt-4 mb-0 text-xs text-muted-foreground">
              Not shown: {hidden.length} that {hidden.length === 1 ? "needs" : "need"} equipment
              you haven&apos;t listed —{" "}
              {hidden
                .slice(0, NAMED_UNAVAILABLE)
                .map((exercise) => `${exercise.name} (${needs(missingEquipment(exercise, settings))})`)
                .join("; ")}
              {hidden.length > NAMED_UNAVAILABLE ? `; and ${hidden.length - NAMED_UNAVAILABLE} more` : ""}.
              {" "}
              <Link to="/training/settings" className="text-primary underline underline-offset-4">
                Change your equipment in Settings
              </Link>
              .
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const exerciseShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  muscles: PropTypes.object,
  requires: PropTypes.arrayOf(PropTypes.string),
});

ExercisePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** The sheet's name: "Swap Bench press", "Add an exercise". */
  title: PropTypes.string.isRequired,
  /** What a pick does ("It takes the 2 sets left; …"). */
  description: PropTypes.string.isRequired,
  /** What can be picked, best first. */
  options: PropTypes.arrayOf(exerciseShape).isRequired,
  /** What was left out for want of equipment (named, not hidden). */
  unavailable: PropTypes.arrayOf(exerciseShape),
  settings: PropTypes.object,
  onPick: PropTypes.func.isRequired,
  /** Show the "Muscle" filter (the add sheet's long list). */
  muscleFilter: PropTypes.bool,
  /** What to say when there's nothing to pick at all. */
  emptyText: PropTypes.string,
  onCloseAutoFocus: PropTypes.func,
};

export default ExercisePicker;
